import React, { useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { apiAddContractRenewal, apiDeleteContractRenewal } from "../../api";
import { toast } from "../../hooks/use-toast";

const fmt = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};
export const daysUntil = (iso) => {
  if (!iso) return null;
  const t = new Date();
  const today = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - today) / 86400000);
};

// Datos de la plataforma + historial de renovaciones pagadas (al registrar, la fecha avanza 1 año).
const PlatformPanel = ({ contract, setPlatform, onSynced, flush }) => {
  const p = contract.platform || {};
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const days = daysUntil(p.renewal_date);

  const register = async () => {
    setBusy(true);
    try {
      await flush();
      const doc = await apiAddContractRenewal(contract.id, { amount: amount === "" ? null : Number(amount), note });
      onSynced(doc);
      setAmount("");
      setNote("");
      toast({ title: "Renovación registrada", description: `Próxima renovación: ${fmt(doc.platform?.renewal_date)}` });
    } catch (e) {
      toast({ title: "No se pudo registrar", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (rid) => onSynced(await apiDeleteContractRenewal(contract.id, rid));

  const field = (k, label, props = {}) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={p[k] || ""} onChange={(e) => setPlatform({ [k]: e.target.value })} {...props} />
    </div>
  );

  return (
    <div className="space-y-3" data-testid="platform-panel">
      <div className="grid grid-cols-2 gap-2">
        {field("name", "Plataforma")}
        {field("monthly_fee", "Cuota mensual (MXN)", { type: "number" })}
        {field("domain", "Dominio")}
        {field("domain_fee", "Costo anual dominio (MXN)", { type: "number" })}
        <div className="col-span-2">
          <Label className="text-xs">Próxima renovación</Label>
          <Input type="date" value={p.renewal_date || ""} onChange={(e) => setPlatform({ renewal_date: e.target.value })} data-testid="platform-renewal-date" />
          {days !== null && (
            <p className={`mt-1 text-xs font-semibold ${days < 0 ? "text-red-600" : days <= 15 ? "text-amber-600" : "text-emerald-600"}`}>
              {days < 0 ? `Vencida hace ${-days} día(s)` : days === 0 ? "Vence hoy" : `Faltan ${days} día(s) · te avisamos 15 días antes`}
            </p>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-emerald-50/70 p-3">
        <p className="mb-2 text-xs font-semibold text-emerald-800">Registrar renovación pagada</p>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" placeholder="Monto" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="renewal-amount" />
          <Input placeholder="Nota (opcional)" value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <Button onClick={register} disabled={busy || !p.renewal_date} className="mt-2 w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="renewal-save">
          <RefreshCw className="h-4 w-4" /> {busy ? "Guardando…" : `Pagada la de ${fmt(p.renewal_date)}`}
        </Button>
      </div>

      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Historial de renovaciones</p>
        {(contract.renewals || []).length === 0 ? (
          <p className="py-3 text-center text-xs text-slate-400">Sin renovaciones registradas.</p>
        ) : (
          <div className="space-y-1.5">
            {contract.renewals.map((r) => (
              <div key={r.id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs" data-testid="renewal-row">
                <span className="flex-1">
                  <b>Renovación {fmt(r.period)}</b>
                  {r.amount ? ` · $${Number(r.amount).toLocaleString("es-MX")}` : ""} · pagada {fmt(r.date)}
                  {r.note ? ` · ${r.note}` : ""}
                </span>
                <button onClick={() => remove(r.id)} className="text-slate-300 hover:text-red-500">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformPanel;
